import { useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import styled from "styled-components";

import { CSS_VARIABLES } from "enums/cssVariables";
import { useTranslation } from "react-i18next";
import { performSignature } from "components/helper/callApi";
import { useUser } from "components/helper/userContext";
import { useHandleErrors } from "./common/handleErrors";
import ErrorPage from "../common/ErrorPage";
import {
    DisplayedError,
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";
import Layout from "../common/Layout";
import LogoutWrapper from "components/common/LogoutWrapper";
import SignatureLogoutButton from "./common/SignatureLogoutButton";
import ProxiesAccordion from "../vote/Votebox/common/AutomaticVoteForProxiesComponents/ProxiesAccordion";

const maxWidth = 400;
const height = 200;
//250 max number of transparent pixels
const SIGNATURE_PIXEL_THRESHOLD = 250;

const Signature = () => {
    const canvasSignatureRef = useRef<HTMLCanvasElement>(null);
    const underLayerSignatureCanvasRef = useRef<HTMLCanvasElement>(null);

    const signaturePadRef = useRef<SignaturePad | null>(null);
    const [isSignaturePadEmpty, setIsSignaturePadEmpty] = useState(true);
    const [isSignatureValid, setIsSignatureValid] = useState(false);

    const { user, setUser } = useUser();
    const [error, setError] = useState<DisplayedError | null>(null);

    const { handleLoginError } = useHandleErrors();

    const { t, i18n } = useTranslation();

    useEffect(() => {
        const signatureCanvas = canvasSignatureRef.current;
        const underLayerCanvas = underLayerSignatureCanvasRef.current;

        const resizeCanvas = () => {
            if (!signatureCanvas || !underLayerCanvas) {
                return;
            }

            signatureCanvas.width = signatureCanvas.parentElement
                ?.offsetWidth as number;
            signatureCanvas.height = signatureCanvas.parentElement
                ?.offsetHeight as number;
            underLayerCanvas.width = signatureCanvas.parentElement
                ?.offsetWidth as number;
            underLayerCanvas.height = signatureCanvas.parentElement
                ?.offsetHeight as number;

            signaturePadRef.current = new SignaturePad(signatureCanvas, {
                minWidth: 1,
                maxWidth: 2,
            });

            const underLayerContext = underLayerCanvas.getContext("2d");

            if (underLayerContext) {
                drawText(underLayerContext);
            }

            if (signaturePadRef.current) {
                signaturePadRef.current.addEventListener("endStroke", () => {
                    setIsSignaturePadEmpty(
                        signaturePadRef.current?.isEmpty() ?? true,
                    );

                    setIsSignatureValid(
                        !isSignatureTooSmall(canvasSignatureRef.current!),
                    );
                });
            }
        };

        // Initial resizing
        resizeCanvas();

        window.addEventListener("resize", resizeCanvas);

        return () => {
            window.removeEventListener("resize", resizeCanvas);
        };
    }, [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    const drawText = (underLayerContext: CanvasRenderingContext2D) => {
        underLayerContext.font =
            "italic 20px 'Helvetica Neue', Arial, sans-serif";
        underLayerContext.fillStyle = "lightgray";

        // Defines horizontal alignment (center)
        underLayerContext.textAlign = "center";
        // Defines vertical alignment (middle)
        underLayerContext.textBaseline = "middle";
        // Defines x and y coords so as to draw texts at center of canvas
        const x = underLayerContext.canvas.width / 2;
        const y = underLayerContext.canvas.height / 2;

        underLayerContext.fillText(t("signature.signHere"), x, y);
        underLayerContext.fillText(user?.displayName as string, x, y + 30);
    };

    const isSignatureTooSmall = (
        canvas: HTMLCanvasElement,
        threshold = SIGNATURE_PIXEL_THRESHOLD,
    ): boolean => {
        //get 2D drawing context from the canvs
        const ctx = canvas.getContext("2d");
        if (!ctx) return true;

        // retreive all the pixls
        const imageData = ctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height,
        ).data;
        let nonEmptyPixels = 0;

        for (let i = 0; i < imageData.length; i += 4) {
            const alpha = imageData[i + 3];
            if (alpha > 0) nonEmptyPixels++;
        }

        return nonEmptyPixels < threshold;
    };

    const handleSignature = async (blob: Blob) => {
        try {
            const refreshedUser = await performSignature(blob);

            setUser(refreshedUser);
        } catch (error) {
            if (error instanceof HTTPResponseError) {
                handleLoginError(error.response.status);
            } else {
                setError(error as NotFoundError | UnknownError);
            }
        }
    };

    const handleClear = () => {
        if (isSignaturePadEmpty) {
            return;
        }

        signaturePadRef.current?.clear();
        setIsSignaturePadEmpty(true);
        setIsSignatureValid(false);
        const underLayerCanvas =
            underLayerSignatureCanvasRef.current as HTMLCanvasElement;
        const underLayerContext = underLayerCanvas.getContext("2d");

        if (underLayerContext) {
            drawText(underLayerContext);
        }
    };

    const handleValidate = async () => {
        if (!isSignaturePadEmpty) {
            setIsSignaturePadEmpty(false);
            const canvas = canvasSignatureRef.current;
            if (!canvas) return;
            const blob: Blob | null = await new Promise((resolve) => {
                canvas.toBlob((b) => resolve(b), "image/png");
            });
            if (!blob) return;
            await handleSignature(blob);
        }
    };

    if (error) {
        return <ErrorPage error={error} />;
    }

    return (
        <>
            <LogoutWrapper>
                <SignatureLogoutButton />
            </LogoutWrapper>
            <Layout>
                <>
                    <Wrapper>
                        <StyledCanvasWrapper>
                            <StyledCanvas
                                ref={canvasSignatureRef}
                                style={{ zIndex: "1" }}
                                aria-label={t("canvasSignature")}
                                role="region"
                            />
                            <StyledCanvas
                                ref={underLayerSignatureCanvasRef}
                                style={{ zIndex: "0", border: "none" }}
                                aria-hidden="true"
                            />
                        </StyledCanvasWrapper>
                        <ButtonWrapper>
                            <StyledOutlineButton
                                onClick={handleClear}
                                aria-label={t("clear")}
                                disabled={
                                    isSignaturePadEmpty || !isSignatureValid
                                }
                            >
                                {t("clear")}
                            </StyledOutlineButton>
                            <StyledButton
                                onClick={handleValidate}
                                disabled={
                                    isSignaturePadEmpty || !isSignatureValid
                                }
                                aria-label={t("validate")}
                            >
                                {t("validate")}
                            </StyledButton>
                        </ButtonWrapper>
                    </Wrapper>
                    {user && user?.proxies.length > 0 && (
                        <ProxiesAccordion>
                            <StyledWrapperProxies>
                                {user?.proxies.map((proxy: any) => {
                                    return (
                                        <div key={proxy.id}>
                                            <p>{proxy.displayName}</p>
                                        </div>
                                    );
                                })}
                            </StyledWrapperProxies>
                        </ProxiesAccordion>
                    )}
                </>
            </Layout>
        </>
    );
};

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: auto;
    max-width: ${maxWidth}px;
    > p {
        margin: auto;
        padding: 2rem 0 1rem 0;
        font-size: 0.75rem;
        line-height: 22px;
        text-align: left;
        width: 620px;
        > a {
            text-decoration: underline;
            color: black;
        }
        @media (max-width: 768px) {
            width: 320px;
        }
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: end;
`;

const StyledWrapperProxies = styled.div`
    padding-left: 1.5rem;
`;

const StyledCanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    height: ${height}px;
`;

const StyledCanvas = styled.canvas`
    border: 1px solid gray;
    cursor: crosshair;
    position: absolute;
    height: 100%;
    width: 100%;
`;

const StyledOutlineButton = styled.button.attrs({ type: "button" })`
    padding: 10px 18px;
    margin: 0.5rem 0.5rem;
    font-size: 18px;
    cursor: pointer;
    background-color: #fafafa;
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
    &:hover {
        filter: brightness(0.8);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        filter: none;
        pointer-events: none;
    }
`;

const StyledButton = styled(StyledOutlineButton)`
    background-color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: #fff;
    margin-right: 0;
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
`;

export default Signature;
